"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommonInquiries() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "How do I book a consultation?",
    answer: "You can easily schedule your appointment through our online booking portal. We recommend a brief consultation for all new color transformations to ensure we achieve your desired look."
  }, {
    question: "What is your policy on color corrections?",
    answer: "Color corrections are bespoke services. We require an in-person assessment to determine the health of your hair and the time required to achieve your goals safely."
  }, {
    question: "Do you offer hair extension services?",
    answer: "Yes, we specialize in premium hand-tied extensions. Please download our complete pricing and extension guide to view our full range of application methods and maintenance requirements."
  }, {
    question: "How should I prepare for my appointment?",
    answer: "For color services, we suggest arriving with clean, dry hair. For deep-conditioning treatments, we will handle the prep work to ensure your hair receives maximum hydration."
  }, {
    question: "What products do you use in the salon?",
    answer: "We exclusively use high-end, salon-grade products that prioritize hair integrity and shine. Our stylists will recommend a personalized regimen to maintain your results at home."
  }, {
    question: "Can I bring a guest with me?",
    answer: "To maintain a relaxing, boutique atmosphere for all our clients, we kindly ask that you arrive unaccompanied for your service."
  }, {
    question: "What is your cancellation policy?",
    answer: "We require 48 hours' notice for any cancellations or rescheduling. This allows us to accommodate other clients waiting for an appointment."
  }, {
    question: "Do you provide bridal styling?",
    answer: "We offer curated bridal packages including trials and day-of styling. Please contact our studio directly to discuss your wedding date and specific requirements."
  }];
  return <section data-section-id="189" id="common-inquiries" className="bg-background py-32 font-default">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              

              <h2 className="font-default text-foreground text-5xl md:text-[3rem] leading-[1.05] tracking-tight mb-8">
                Studio{" "}
                <span className="italic font-light">Inquiries.</span>
              </h2>

              <p className="font-default text-muted-foreground text-lg leading-relaxed max-w-md mb-10">
                Everything you need to know about your visit to Velvet Shears — from
                booking your transformation to maintaining your style.
              </p>

              <Link to="/BookPage" className="inline-flex items-center gap-2 font-default text-foreground text-base border-b border-foreground/40 pb-1 hover:border-foreground transition-colors duration-300">
                <span>Still have questions? Contact us</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={i} value={`item-${i}`} className="border-b border-border last:border-b-0 group data-[state=open]:bg-accent/40 transition-colors duration-400 ease-out">
                  <AccordionTrigger className="hover:no-underline py-7 px-2 md:px-4 [&>svg]:hidden group/trigger">
                    <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                      <span className="font-mono text-xs tracking-[0.2em] text-primary pt-2 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default text-foreground text-xl md:text-[1.25rem] leading-snug font-normal">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-5 shrink-0 text-foreground/60 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45 mt-2" strokeWidth={1.25} />
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 px-2 md:px-4">
                    <div className="flex gap-5 md:gap-8">
                      <span className="font-mono text-xs shrink-0 opacity-0 select-none">
                        00
                      </span>
                      <p className="font-default text-muted-foreground text-base md:text-[1.0625rem] leading-[1.75] max-w-2xl">
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}